// AgentClient — thin wrapper around POST /infer với SSE streaming.

import { parseSseStream } from "./stream";
import { GatewayError, type ChatMessage, type InferConfig, type StreamEvent, type Tool } from "./types";

export interface AgentClientOptions {
  baseUrl: string;
  apiKey: string;
  /// User identifier. Có thể là function để re-evaluate mỗi request (vd lấy từ JWT).
  userId?: string | (() => string | null | undefined);
  /// Headers extra để gửi kèm mọi request (vd telemetry).
  extraHeaders?: Record<string, string>;
}

export class AgentClient {
  private baseUrl: string;
  private apiKey: string;
  private userId?: string | (() => string | null | undefined);
  private extraHeaders: Record<string, string>;

  constructor(opts: AgentClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.apiKey = opts.apiKey;
    this.userId = opts.userId;
    this.extraHeaders = opts.extraHeaders ?? {};
  }

  private resolveUserId(): string | null {
    if (!this.userId) return null;
    return typeof this.userId === "function" ? this.userId() ?? null : this.userId;
  }

  /// Stream 1 turn /infer. Trả async generator các StreamEvent.
  async *stream(input: {
    messages: ChatMessage[];
    tools?: Pick<Tool, "name" | "description" | "parameters">[];
    config?: InferConfig;
    signal?: AbortSignal;
  }): AsyncGenerator<StreamEvent, void, unknown> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.apiKey}`,
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...this.extraHeaders,
    };
    const uid = this.resolveUserId();
    if (uid) headers["X-User-Id"] = uid;

    const body = JSON.stringify({
      messages: input.messages,
      ...(input.tools ? { tools: input.tools.map(({ name, description, parameters }) => ({ name, description, parameters })) } : {}),
      ...(input.config ? { config: input.config } : {}),
    });

    const resp = await fetch(`${this.baseUrl}/infer`, {
      method: "POST",
      headers,
      body,
      signal: input.signal,
    });

    if (!resp.ok) {
      let errMsg = resp.statusText;
      let code = "http_error";
      try {
        const j = (await resp.json()) as { error?: string; code?: string };
        errMsg = j.error ?? errMsg;
        code = j.code ?? code;
      } catch {
        /* ignore */
      }
      throw new GatewayError(errMsg, resp.status, code);
    }
    if (!resp.body) throw new GatewayError("empty response body", 500, "internal");

    yield* parseSseStream(resp.body);
  }
}
